<template>
    <div class="q-pa-sm FrequencyDurationDocked">
        <div class="row items-center q-mt-sm q-mb-sm">
            <div class="text-primary">
                {{ $t('visualization.customPlotPanels.frequencyAndDuration') }}
            </div>
        </div>

        <div class="row q-col-gutter-md">
            <div class="col-12 col-md-auto FrequencyDurationOptions">
                <div class="row q-col-gutter-sm">
                    <div class="col-12">
                        <QSelect
                            v-model="duration"
                            dense
                            class="selectDuration"
                            :options="durationOptions"
                            :label="$t('visualization.customPlotPanels.frequencyDuration.selectDuration')"
                            @update:modelValue="getData"
                        />
                    </div>
                    <div class="col-12">
                        <QSelect
                            v-model="range"
                            dense
                            class="selectRange"
                            :options="rangeOptions"
                            :label="$t('visualization.customPlotPanels.frequencyDuration.selectRange')"
                            @update:modelValue="getData"
                        />
                    </div>
                </div>
            </div>

            <div class="col-12 col-md FrequencyDurationCharts">
                <div
                    v-if="!emptyRows"
                    class="chart-container"
                >
                    <v-chart
                        :option="barChartOptions"
                        class="chart"
                        autoresize
                    />
                </div>
                <div
                    v-else
                    class="flex flex-center q-mx-md q-my-lg"
                >
                    <WText>
                        {{ $t('visualization.customPlotPanels.attributesInfo.noData') }}
                    </WText>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import { filtersMixin, filtersStorageMixin, dashboardChartsMixin } from '@/mixins'
import {
    Api,
    apiRequest,
    notifyError,
} from '@/api'
import echartsColors from '@/charts/echartsColors'

const DEFAULT_TAB = 'FREQUENCY'
export default {
    name: 'FrequencyDurationPanelDocked',
    components: { },
    mixins: [filtersMixin, filtersStorageMixin, dashboardChartsMixin],
    props: {
        currentTitle: VueTypes.object,
        filters: VueTypes.array,
        chart: VueTypes.object,
        processId: VueTypes.string,
    },
    emits: ['title', 'chartLoading'],
    data() {
        return {
            title: this.currentTitle,
            selected: false,
            emptyRows: false,
            rangeString: '',
            model: null,
            modifiedFilters: [],
            duration: {
                key: 'MONTH',
                label: this.$t('visualization.customPlotPanels.attributesInfo.month'),
            },
            durationOptions: [
                {
                    key: 'MONTH',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.month'),
                },
                {
                    key: 'WEEK',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.week'),
                },
                {
                    key: 'DAY',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.day'),
                },
            ],
            rangeOptions: [
                {
                    key: 'START',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.start'),
                },
                {
                    key: 'END',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.end'),
                },
            ],
            range: {
                key: 'START',
                label: this.$t('visualization.customPlotPanels.attributesInfo.start'),
            },
            metricOptions: [{
                key: 'FD', label: this.$t('visualization.customPlotPanels.frequencyAndDuration'), disabled: true, icon: 'timelapse',
            },
            {
                key: 'AI', label: this.$t('visualization.customPlotPanels.attributeInfo'), disabled: false, icon: 'query_stats',
            }],
            currentSel: DEFAULT_TAB,
            maxDur: '',
            barChartOptions: {
                title: {
                    text: '',
                },
                tooltip: {
                    trigger: 'axis',
                    confine: true,
                    formatter: (params) => {
                        let tooltipContent = ''
                        const { name } = params[0]
                        tooltipContent += `${name}<br>`
                        params.forEach((param) => {
                            const { seriesName } = param
                            const { value } = param
                            const { color } = param
                            tooltipContent += `<div style="width: 10px; height: 10px; background-color: ${color};border-radius: 50%; display: inline-block; margin-right: 3px;">
                                </div>`
                            if (seriesName === this.$t('visualization.customPlotPanels.duration')) {
                                tooltipContent += `${seriesName}: ${moment.duration(value, 'seconds').format(
                                    'd[d] h[h]:m[m]:s[s]',
                                    { largest: 2, trim: false },
                                )} <br>`
                            } else {
                                tooltipContent += `${seriesName}: ${value} <br>`
                            }
                        })
                        return tooltipContent
                    },
                },
                dataZoom: [
                    {
                        type: 'inside',
                        show: true,
                        start: 0,
                        end: 100,
                        handleSize: 8,
                    },
                ],
                xAxis: {
                    type: 'category',
                    data: [],
                },
                yAxis: [
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: (value) => this.formatNumber(value),
                        },
                    },
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: (value) => `${this.formatDuration(value, 1, 1)}`,
                        },
                    },
                ],
                grid: {
                    left: 45,
                    right: 45,
                    bottom: 23,
                    top: 20,
                },
                series: [
                    {
                        name: this.$t('visualization.customPlotPanels.duration'),
                        type: 'line',
                        data: [],
                        itemStyle: {
                            color: echartsColors.line.stroke,
                        },
                        yAxisIndex: 1,
                    },
                    {
                        name: this.$t('visualization.customPlotPanels.frequency'),
                        type: 'bar',
                        data: [],
                        itemStyle: {
                            color: echartsColors.bar.fill,
                        },
                        yAxisIndex: 0,
                    },
                ],
            },
        }
    },
    watch: {
        filters(newValue, oldValue) {
            let equal = false
            if (newValue.length === oldValue.length) {
                if (newValue.length === 0) {
                    equal = true
                } else {
                // eslint-disable-next-line no-plusplus
                    for (let i = 0; i < newValue.length; i++) {
                        if (newValue[i] !== oldValue[i]) {
                            equal = true
                        }
                    }
                }
            }
            if (equal === false) {
                this.getData()
            }
        },
        chart() {
            this.getData()
        },
    },
    mounted() {
        this.getData()
    },
    methods: {
        setSelected() {
            this.selected = !this.selected
        },
        handleClickOption(sel) {
            this.$emit('title', sel)
            this.setSelected()
        },
        getData() {
            this.$emit('chartLoading', true)
            let data = {
                duration: this.duration.key,
                caseType: this.range.key,
                limit: 8,
                start: 0,
            }
            if (this.chart.variants || this.chart.variantId || this.chart.loopId || this.chart.loopId === 0) {
                data = {
                    ...data,
                    ...(this.chart.variantId ? { variantUid: this.chart.variantId } : {}),
                    ...(this.chart.variants ? { variantCount: this.chart.variants } : {}),
                    ...(this.chart.loopId || this.chart.loopId === 0 ? { loopUid: this.chart.loopId } : {}),
                }
            } else {
                this.emptyRows = true
                this.$emit('chartLoading', false)
                return
            }
            data = this.buildVisualizationParams(data)
            apiRequest(Api().visualizations.frequencyPanel({ processId: this.processId, params: data }))
                .then((res) => {
                    const returnData = res
                    if (returnData.rows.length === 0) {
                        this.emptyRows = true
                    } else {
                        const frequencyArr = [returnData.rows.map((item) => item.frequency)][0]
                        const commonAttributes = [returnData.rows.map((item) => item.timeString)][0]
                        this.barChartOptions.series[1].data = frequencyArr
                        this.barChartOptions.series[0].data = returnData.rows.map((item) => item.duration)
                        this.barChartOptions.xAxis.data = commonAttributes
                    }
                })
                .catch(notifyError)
                .finally(() => {
                    this.$emit('chartLoading', false)
                })
        },
    },
}
</script>
<style lang="scss" scoped>
.FrequencyDurationOptions {
    width: 250px;
}

.selectRange {
    width: 100%;
    height: auto;
    margin: 0;
}

.selectDuration {
    width: 100%;
    height: auto;
    margin: 0;
}

.chart-container {
    margin: 0 auto;
    width: 100%;

    .chart {
        width: 100%;
        height: 200px;
        margin-bottom: 5px;

        & + .chart {
            margin-left: 30px;

            @media screen and (max-width: $lg) {
                margin-left: 0px;
            }
        }
    }

    .openIcon {
        height: fit-content;
        padding-bottom: 0px;
        margin-left: 0 auto;
    }
}

:deep(.Dropdown .Items) {
    gap: 10px;
    white-space: nowrap;
    flex-wrap: nowrap;
}

:deep(.Dropdown .Items .LeftIcon) {
    margin-right: 0;
    display: flex;
    flex-direction: column;
}

:deep(.Dropdown .Items .Text) {
    align-items: center;
    display: flex;
    flex-direction: column;
}
</style>
